import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  driverHasLeftAcceptedDelivery,
  driverHasAutoOpenedAcceptedDelivery,
  markDriverAutoOpenedAcceptedDelivery,
  OFFER_RING_CYCLE_MS,
  OPEN_OFFER_MAX_AGE_MS,
} from '../../lib/driverIncomingBookings';
import {
  getDriverOnlinePreference,
  getDriverSession,
  setDriverOnlinePreference,
} from '../../lib/driverSession';
import { fetchDriverNotifPrefs, readCachedDriverNotifPrefs } from '../../lib/driverNotificationPrefs';
import { playDriverNewOfferRing, unlockDriverOfferAudio, notifyDriverNewOffer, startDriverOfferRing, stopDriverOfferRing, stopAllDriverOfferRings, handleDriverOfferStopSignal, getActiveDriverOfferRingKeys } from '../../lib/driverOfferRing';
import { riderWork } from '../../api/riderWork';
import {
  acceptApiOffer,
  API_DELIVERIES,
  declineApiOffer,
  isCarried,
  jobFromApi,
  offerFromApi,
  ORDER_ALREADY_ACCEPTED_MSG,
  recentFromApi,
} from '../../lib/riderWork';
import { useLiveLocation } from '../../hooks/useLiveLocation';
import { IconClose } from '../icons/LineIcons';
import { jobPath } from './useDriverJob';
import { useJourneyPings } from './useJourneyPings';
import { markPositionsDisclosed, positionsDisclosed, recordsNatively, startOnDuty, stopRecording } from '../../lib/backgroundPositions';
import { batteryAsk } from '../../lib/batterySetup';
import { openBatterySettings } from '../../lib/nativePermissions';
import ConfirmDialog from '../ConfirmDialog';
import './driverNewOfferNotifier.css';

const POLL_MS = 2500;
const AUTO_DISMISS_MS = 14000;

const DriverOffersContext = createContext(null);

function offerKey(o) {
  return `${o.table}:${o.id}`;
}

/** Route of the full-screen offer for a `table:id` key or a bare order id. */
export function offerPath(key) {
  return `/driver/offer/${encodeURIComponent(String(key || ''))}`;
}

/**
 * The offer a push tag, offer key or route param refers to, or undefined. One
 * matcher for the notification buttons and the offer screen: a key may be
 * `table:id`, a bare id, or a tag ending in the id.
 */
export function findOfferForKey(offers, key, tag = '') {
  const k = String(key || '');
  const t = String(tag || '');
  return offers.find((o) => {
    const id = String(o.id);
    return offerKey(o) === k || id === k || (t !== '' && t.endsWith(id)) || (k !== '' && k.endsWith(id));
  });
}

function isNativeApp() {
  try {
    return Boolean(window.Capacitor?.isNativePlatform?.());
  } catch {
    return false;
  }
}

/** Hard cap on how long a notification button waits for its offer; the real gate is two completed polls. */
const NOTIFICATION_ACTION_WAIT_MS = 90000;

/** Withdraw the OS notification for an offer once a button on it has been acted on. */
function withdrawOfferNotification(tag) {
  if (!tag) return;
  void import('../../lib/driverPush')
    .then((m) => m.removeDeliveredOfferNotification(tag))
    .catch(() => {});
}

function placeLabel(raw) {
  const t = String(raw ?? '').trim();
  if (!t) return '—';
  return t.split(',')[0].trim().slice(0, 42) || '—';
}

/** Every offer InGo makes a rider is a delivery. */
const OFFER_TITLE = 'New delivery request';

/** Which 2‑minute (or configured) re-offer cycle an offer is in. */
function offerCycleIndex(iso) {
  const placed = new Date(iso || 0).getTime();
  if (!Number.isFinite(placed) || placed <= 0) return -1;
  const elapsed = Date.now() - placed;
  if (elapsed < 0 || elapsed >= OPEN_OFFER_MAX_AGE_MS) return -1;
  return Math.floor(elapsed / OFFER_RING_CYCLE_MS);
}

function shortAmount(n) {
  const v = Number(n);
  if (!Number.isFinite(v)) return '';
  return `$${v.toFixed(2)}`;
}

/**
 * `work` is InGo's rider API (`src/api/riderWork.js`); a test hands in a fake.
 */
export function DriverOffersProvider({ children, work = riderWork, battery = batteryAsk }) {
  const navigate = useNavigate();
  const location = useLocation();
  const session = getDriverSession();
  const driverId = session?.id || null;

  const [online, setOnlineState] = useState(() => getDriverOnlinePreference());
  const [offers, setOffers] = useState([]);
  const [recent, setRecent] = useState([]);
  const [loadErr, setLoadErr] = useState('');
  const [toasts, setToasts] = useState([]);
  const [ringingOfferKeys, setRingingOfferKeys] = useState(() => new Set());
  const [takenNotice, setTakenNotice] = useState('');
  /** Outcome of an Accept / Decline pressed on the notification itself. */
  const [actionNotice, setActionNotice] = useState('');
  const [actionTick, setActionTick] = useState(0);
  const [, setSecTick] = useState(0);

  // One GPS watch for the whole driver area. Until 2026-09-03 DriverHomePage owned
  // the watch and the 15 s publish loop, and its unmount cleanup published
  // is_online=false — so opening any other tab, or the offer screen, took the
  // driver out of the dispatch pool (found when the driver row read
  // is_online=false with a stale position after a ring). Now the loop lives
  // here, under every /driver/* route, and the offline publish happens only
  // when the driver goes offline or leaves the driver area.
  const live = useLiveLocation({ mapThrottleMs: 12000, movePublishMeters: 80 });
  const liveRef = useRef(live);
  liveRef.current = live;

  /** Why the switch is off when the rider asked for on, or the platform took them off. */
  const [dutyNotice, setDutyNotice] = useState('');
  /**
   * The server's last word on duty: null until it has answered. Only this starts
   * the heartbeat. The stored switch says "online" on a fresh install, so a fix
   * arriving before the server's answer was sent, and refused as NOT_ON_DUTY,
   * which turned the switch off (InGo's debug build on an emulator, 30 Sep 2026).
   */
  const [dutyOn, setDutyOn] = useState(null);
  /** How often the server wants the position (its `heartbeat_seconds`). */
  const heartbeatSecondsRef = useRef(15);
  const tickCountRef = useRef(0);

  const onlineRef = useRef(online);
  const knownOfferKeysRef = useRef(new Set());
  const offersPrimedRef = useRef(false);
  const cycleRingedRef = useRef(new Map());
  /** Offer keys currently ringing — stopped only when backend says the trip is gone. */
  const activeRingKeysRef = useRef(new Set());
  const navigateRef = useRef(navigate);
  navigateRef.current = navigate;
  const pathRef = useRef(location.pathname);
  pathRef.current = location.pathname;
  /** A notification button press waiting for its offer: { action, offerKey, tag, at }. */
  const pendingActionRef = useRef(null);
  /** Completed offer polls, success or failure. Lets a parked action wait for real data, not a clock. */
  const pollsDoneRef = useRef(0);

  useEffect(() => {
    onlineRef.current = online;
  }, [online]);

  const applyDuty = useCallback((duty) => {
    if (!duty) return;
    const seconds = Number(duty.heartbeat_seconds);
    if (Number.isFinite(seconds) && seconds > 0) heartbeatSecondsRef.current = seconds;
    const on = Boolean(duty.on_duty);
    setDutyOn(on);
    if (on !== onlineRef.current) {
      onlineRef.current = on;
      setOnlineState(on);
      setDriverOnlinePreference(on);
    }
  }, []);

  // The server's switch is the truth (InGo's API, M5): read once per sign-in,
  // so a phone left "online" after the platform ended the shift says so.
  useEffect(() => {
    if (!driverId) return undefined;
    let cancelled = false;
    let retry = null;
    const read = () =>
      work.duty().then(
        (duty) => !cancelled && applyDuty(duty),
        (e) => {
          console.warn(`[DriverOffers] duty read failed ${JSON.stringify({ code: e?.code || null, message: e?.message || String(e) })}`);
          // Could not ask is not "off duty": ask again, and send no position meanwhile.
          if (!cancelled) retry = window.setTimeout(read, Math.max(5, heartbeatSecondsRef.current) * 1000);
        },
      );
    read();
    return () => {
      cancelled = true;
      if (retry) window.clearTimeout(retry);
    };
  }, [driverId, applyDuty, work]);

  // The position heartbeat while on duty, at the server's pace. `located`, not
  // `hasFix`: a coordinate cached from an earlier session must never be sent as
  // where the rider is now; with no fix the beat waits, and says so.
  // The effect runs again when a fix arrives, so the first one goes at once: a
  // phone's GPS takes seconds to find itself, the beat sent on going online had
  // nothing, and the next is a minute away. Until then the rider was on duty and
  // invisible to dispatch (InGo's debug build on an emulator, 30 Sep 2026).
  const located = Boolean(live.located);
  useEffect(() => {
    if (!driverId || !online || dutyOn !== true) return undefined;
    let stopped = false;
    const beat = async () => {
      if (stopped) return;
      const { lat, lng, located, accuracy, headingDeg } = liveRef.current;
      if (!located || lat == null || lng == null) {
        console.info('[DriverOffers] on duty with no fix yet: no heartbeat sent');
        return;
      }
      try {
        await work.heartbeat({
          latitude: lat,
          longitude: lng,
          accuracyMetres: Number.isFinite(Number(accuracy)) ? Math.round(Number(accuracy)) : null,
          headingDegrees: Number.isFinite(Number(headingDeg)) ? Math.round(Number(headingDeg)) % 360 : null,
          recordedAt: new Date().toISOString(),
        });
      } catch (e) {
        if (e?.code === 'NOT_ON_DUTY') {
          // The platform ended the shift: the switch shows it, never keeps beating.
          stopped = true;
          onlineRef.current = false;
          setDutyOn(false);
          setOnlineState(false);
          setDriverOnlinePreference(false);
          setDutyNotice(e.message || 'You are off duty now.');
          return;
        }
        console.warn(`[DriverOffers] heartbeat failed ${JSON.stringify({ code: e?.code || null, message: e?.message || String(e) })}`);
      }
    };
    void beat();
    const id = window.setInterval(() => void beat(), Math.max(5, heartbeatSecondsRef.current) * 1000);
    return () => {
      stopped = true;
      window.clearInterval(id);
    };
  }, [driverId, online, work, located, dutyOn]);

  // While a job is carried, its position every few seconds, from the same
  // watch: what the client's map follows (M6's foreground half, M7).
  useJourneyPings({ driverId, liveRef, located, work });

  // With the phone locked, the position is recorded natively (M6): started
  // with the server's word that the rider is on duty, while the app is on
  // screen, after the rider has read once what is shared; stopped off duty
  // and at sign-out. Declined, the app records only while it is open.
  const [positionsAsk, setPositionsAsk] = useState(false);
  // A phone that may put InGo to sleep, told at the rider's own "Go online"
  // (M6, sequence D; Kangaru's batteryPrompt): one sentence and the setting.
  const [batteryNotice, setBatteryNotice] = useState('');
  useEffect(() => {
    if (!driverId || dutyOn === false) {
      setPositionsAsk(false);
      void stopRecording();
      return;
    }
    if (dutyOn !== true || !recordsNatively()) return;
    if (!positionsDisclosed()) {
      setPositionsAsk(true);
      return;
    }
    void startOnDuty();
  }, [driverId, dutyOn]);

  // Blueprint piece 7 — act on a notification tap.
  //
  // Two arrivals to handle. A cold start, where the OS launched the app into the
  // push listener before this component existed and driverPushBootstrap parked the
  // tap; and a warm tap while the app is already running, which arrives as an event.
  // Both land here, and both are routed once.
  useEffect(() => {
    // One tap can arrive twice: parked by driverPushBootstrap on a cold start
    // and again as the window event. Drop the duplicate, and nothing else. The
    // previous "handle once per mount" guard silently ignored every button press
    // after the first for as long as the app process lived (found 2026-09-03 in
    // a live demo: the second Accept of the day did nothing).
    const last = { key: '', at: 0 };
    const goToOffer = (detail) => {
      if (!detail) return;
      // The Permissions panel's sample offer (IngoPermissionsPlugin.sendTestOffer)
      // names no order. A tap on it just brings the app up.
      if (String(detail.offerKey || '') === 'test' || String(detail.tag || '') === 'ingo-offer-test') {
        // Its Accept / Decline carry no order, so the only honest response is to
        // clear the notification; a real one is withdrawn after the action runs.
        console.info(`[DriverOffers] test offer ${detail.action || 'tapped'}; nothing to route`);
        withdrawOfferNotification('ingo-offer-test');
        return;
      }
      const key = `${detail.tag || detail.offerKey || detail.link || ''}|${detail.action || ''}`;
      const now = Date.now();
      if (key === last.key && now - last.at < 3000) return;
      last.key = key;
      last.at = now;
      const action = String(detail.action || '');
      if (action === 'accept' || action === 'decline') {
        // A button on the notification itself (OfferMessagingService, Android).
        // Parked here; the effect further down acts on it once the offer is in
        // `offers`. The payload is never trusted for the offer itself: an order
        // somebody else took resolves to "already accepted", not to a stale claim.
        pendingActionRef.current = {
          action,
          offerKey: String(detail.offerKey || ''),
          tag: String(detail.tag || ''),
          at: Date.now(),
          pollsAtPark: pollsDoneRef.current,
        };
        console.info(`[DriverOffers] notification button ${JSON.stringify(pendingActionRef.current)}`);
        setActionTick((n) => n + 1);
      }
      // Any tap that names an offer opens the offer screen; the payload's link
      // is only the fallback for a tap that names nothing.
      const ref = String(detail.offerKey || '').trim() || String(detail.tag || '').replace(/^ingo-offer-/, '').trim();
      const link = ref ? offerPath(ref) : String(detail.link || '/driver/home');
      console.info('[DriverOffers] routing to tapped offer', link);
      // The tap deliberately carries only a link and an offer key. The offer itself
      // is re-fetched by the poll below, so an offer somebody else already took
      // opens to "already accepted" rather than to stale detail from the payload.
      try {
        navigateRef.current(link.startsWith('/') ? link : `/${link}`);
      } catch {
        /* router not ready; the poll still surfaces the offer */
      }
    };

    void import('../../lib/driverPushBootstrap')
      .then((m) => goToOffer(m.consumePendingOfferTap()))
      .catch(() => {});

    const onTap = (e) => {
      goToOffer(e?.detail);
      // The bootstrap parked a copy before dispatching this event. Clear it so a
      // later mount does not replay an old button press.
      void import('../../lib/driverPushBootstrap')
        .then((m) => m.consumePendingOfferTap())
        .catch(() => {});
    };
    window.addEventListener('ingo-driver-offer-tap', onTap);
    return () => window.removeEventListener('ingo-driver-offer-tap', onTap);
  }, []);

  // The switch asks the server, which may refuse (not approved, suspended):
  // shown at once, then set to what the server answered.
  const setOnline = useCallback(
    (next) => {
      const value = typeof next === 'function' ? next(onlineRef.current) : Boolean(next);
      onlineRef.current = value;
      setOnlineState(value);
      setDriverOnlinePreference(value);
      if (value) {
        void battery().then((sentence) => sentence && setBatteryNotice(sentence), () => {});
      }
      // Going offline, the phone stops recording before the server revokes its
      // location token: a fix posted in between went out with a revoked token,
      // a 401 in the log (the emulator, 2 Oct 2026).
      const ready = value ? Promise.resolve() : stopRecording().catch(() => {});
      ready.then(() => work.setDuty(value)).then(applyDuty, (e) => {
        console.warn(`[DriverOffers] duty change refused ${JSON.stringify({ to: value, code: e?.code || null })}`);
        if (value) {
          onlineRef.current = false;
          setOnlineState(false);
          setDriverOnlinePreference(false);
        }
        setDutyNotice(e?.message || 'Your duty could not be changed. Check your connection and try again.');
      });
    },
    [applyDuty, work, battery],
  );

  useEffect(() => {
    const unlock = () => unlockDriverOfferAudio();
    window.addEventListener('pointerdown', unlock, { once: true, passive: true });
    window.addEventListener('keydown', unlock, { once: true });
    return () => {
      window.removeEventListener('pointerdown', unlock);
      window.removeEventListener('keydown', unlock);
    };
  }, []);

  useEffect(() => {
    if (!driverId) return undefined;
    let cancelled = false;
    void fetchDriverNotifPrefs(driverId).then(() => {
      if (cancelled) return;
    });
    return () => {
      cancelled = true;
    };
  }, [driverId]);

  const stopRingForKey = useCallback((key) => {
    if (!key) return;
    activeRingKeysRef.current.delete(key);
    stopDriverOfferRing(key);
    setRingingOfferKeys((prev) => {
      if (!prev.has(key)) return prev;
      const next = new Set(prev);
      next.delete(key);
      return next;
    });
  }, []);

  const syncRingsToOpenOffers = useCallback(
    (openKeys) => {
      const open = openKeys instanceof Set ? openKeys : new Set(openKeys || []);
      // Every ring that is sounding, not only the ones this provider started: a
      // push (driverPush.js → notifyDriverNewOffer) starts its own loop under the
      // same key, and when the offer then vanished because another device or
      // the customer answered it, that loop was never in activeRingKeysRef and
      // rang on for its full two minutes ("the sound keeps ringing after the
      // order was closed", a real phone, 2026-09-06). The shade's copy of the
      // notification goes too: its buttons would only answer "no longer open".
      const sounding = new Set([...activeRingKeysRef.current, ...getActiveDriverOfferRingKeys()]);
      for (const key of sounding) {
        if (!open.has(key)) {
          stopRingForKey(key);
          withdrawOfferNotification(`ingo-offer-${key}`);
        }
      }
    },
    [stopRingForKey],
  );

  const alertNewOffers = useCallback((newOffers, { toast } = { toast: true }) => {
    if (!newOffers.length) return;
    const prefs = readCachedDriverNotifPrefs(driverId);
    if (!prefs.new_offers) return;

    const first = newOffers[0];
    const amountBit = shortAmount(first.amount);
    const firstKey = offerKey(first);
    notifyDriverNewOffer({
      title: OFFER_TITLE,
      body: [
        first.ref ? `#${first.ref}` : null,
        placeLabel(first.from),
        placeLabel(first.to) !== '—' ? `→ ${placeLabel(first.to)}` : null,
        amountBit || null,
        newOffers.length > 1 ? `(+${newOffers.length - 1} more)` : null,
      ]
        .filter(Boolean)
        .join(' · '),
      tag: `ingo-offer-${firstKey}`,
      offerKey: firstKey,
      onClickPath: '/driver/home',
      sound: prefs.offer_sound,
      banner: true,
      loop: true,
      maxMs: OFFER_RING_CYCLE_MS,
    });
    // Extra one-shot burst if more than one new offer arrived together
    if (prefs.offer_sound && newOffers.length > 1) {
      window.setTimeout(() => playDriverNewOfferRing(), 900);
    }
    const keys = newOffers.map(offerKey);
    for (const k of keys) {
      activeRingKeysRef.current.add(k);
      if (prefs.offer_sound && k !== firstKey) {
        startDriverOfferRing(k, { maxMs: OFFER_RING_CYCLE_MS });
      }
    }
    setRingingOfferKeys((prev) => {
      const next = new Set(prev);
      for (const k of keys) next.add(k);
      return next;
    });

    if (isNativeApp()) {
      // In the app the request is a screen, not a toast (2026-09-03, client).
      // A re-ring re-opens it unless the driver is already looking at it.
      const target = offerPath(firstKey);
      if (pathRef.current !== target) {
        try {
          navigateRef.current(target);
        } catch {
          /* router not ready; the poll still surfaces the offer on home */
        }
      }
      return;
    }

    if (!toast) return;
    setToasts((prev) => [
      ...prev,
      ...newOffers.map((o) => ({
        key: offerKey(o),
        title: OFFER_TITLE,
        ref: o.ref,
        pickup: placeLabel(o.from),
        dropoff: placeLabel(o.to),
        amount: shortAmount(o.amount),
      })),
    ]);
  }, [driverId]);

  const dismissToast = useCallback((key) => {
    setToasts((prev) => prev.filter((t) => t.key !== key));
  }, []);

  const tickRef = useRef(async () => {});

  useEffect(() => {
    tickRef.current = async () => {
      if (!driverId) {
        setOffers([]);
        setRecent([]);
        return;
      }

      try {
        tickCountRef.current += 1;

        // The rider's jobs for "Your Recent Jobs": every fourth poll is plenty.
        if (tickCountRef.current % 4 === 1) {
          setRecent((await work.deliveries()).map(recentFromApi));
        }

        // The job carried now, opened once per session when the rider is
        // elsewhere (a restart, an accept from the notification). Never over a
        // job screen the rider is already on.
        const carried = await work.current();
        if (
          carried &&
          isCarried(carried) &&
          !driverHasLeftAcceptedDelivery(API_DELIVERIES, carried.id) &&
          !driverHasAutoOpenedAcceptedDelivery(API_DELIVERIES, carried.id) &&
          !/^\/driver\/(active-delivery|navigation|confirm-pickup|collect-payment)/.test(pathRef.current)
        ) {
          markDriverAutoOpenedAcceptedDelivery(API_DELIVERIES, carried.id);
          const job = jobFromApi(carried);
          navigateRef.current(jobPath('active-delivery', job), { state: { order: job } });
        }

        if (!onlineRef.current) {
          setOffers([]);
          knownOfferKeysRef.current = new Set();
          offersPrimedRef.current = false;
          cycleRingedRef.current = new Map();
          activeRingKeysRef.current = new Set();
          stopAllDriverOfferRings();
          setRingingOfferKeys(new Set());
          setLoadErr('');
          return;
        }

        // The live offers, from InGo's dispatch: its own waves, radius and
        // vehicle rules chose this rider; the app filters nothing.
        const next = (await work.offers()).map(offerFromApi);
        setOffers(next);

        const keys = next.map(offerKey);
        const current = new Set(keys);

        // Backend is source of truth: stop any ring whose offer is no longer open
        // (accepted by anyone, cancelled, expired past open window).
        syncRingsToOpenOffers(current);

        if (!offersPrimedRef.current) {
          knownOfferKeysRef.current = current;
          offersPrimedRef.current = true;
          for (const o of next) {
            cycleRingedRef.current.set(offerKey(o), offerCycleIndex(o.created_at));
          }
          setLoadErr('');
          return;
        }

        const brandNew = next.filter((o) => !knownOfferKeysRef.current.has(offerKey(o)));
        knownOfferKeysRef.current = current;

        if (brandNew.length) {
          for (const o of brandNew) {
            cycleRingedRef.current.set(offerKey(o), offerCycleIndex(o.created_at));
          }
          // Always show in-app banner (native WebViews often block system Notification API).
          alertNewOffers(brandNew, { toast: true });
        } else {
          // Re-ring when a still-open offer enters a new cycle (was advertised but never wired).
          const reRing = [];
          for (const o of next) {
            const k = offerKey(o);
            const cycle = offerCycleIndex(o.created_at);
            if (cycle < 0) continue;
            const prevCycle = cycleRingedRef.current.get(k);
            if (prevCycle == null) {
              cycleRingedRef.current.set(k, cycle);
              continue;
            }
            if (cycle > prevCycle) {
              cycleRingedRef.current.set(k, cycle);
              reRing.push(o);
            }
          }
          if (reRing.length) alertNewOffers(reRing, { toast: true });
        }

        // Drop cycle map entries for offers that disappeared
        for (const k of [...cycleRingedRef.current.keys()]) {
          if (!current.has(k)) cycleRingedRef.current.delete(k);
        }

        setLoadErr('');
      } catch (e) {
        setLoadErr(e?.message || String(e));
      } finally {
        pollsDoneRef.current += 1;
        // A parked notification action re-evaluates after every poll, even one
        // that returned the same (possibly empty) list.
        if (pendingActionRef.current) setActionTick((n) => n + 1);
      }
    };
  }, [driverId, location.pathname, alertNewOffers, syncRingsToOpenOffers, work]);

  // The poll is the transport (background-push rule 3): every offer and every
  // job the rider sees is read here. A push, once InGo sends one, only makes
  // it sooner.
  useEffect(() => {
    if (!driverId) return undefined;
    let cancelled = false;
    let timer = null;

    const run = async () => {
      if (cancelled) return;
      await tickRef.current();
    };

    run();
    timer = window.setInterval(run, POLL_MS);

    const onVis = () => {
      if (document.visibilityState === 'visible') run();
    };
    document.addEventListener('visibilitychange', onVis);
    window.addEventListener('focus', onVis);

    const onPushStop = (ev) => {
      const detail = ev?.detail || {};
      handleDriverOfferStopSignal(detail.offerKey, detail.tag);
    };
    window.addEventListener('ingo-driver-offer-stop', onPushStop);

    return () => {
      cancelled = true;
      if (timer) window.clearInterval(timer);
      document.removeEventListener('visibilitychange', onVis);
      window.removeEventListener('focus', onVis);
      window.removeEventListener('ingo-driver-offer-stop', onPushStop);
      stopAllDriverOfferRings();
      activeRingKeysRef.current = new Set();
    };
  }, [driverId, online]);

  useEffect(() => {
    if (!online) return undefined;
    const id = window.setInterval(() => setSecTick((n) => n + 1), 1000);
    return () => window.clearInterval(id);
  }, [online]);

  useEffect(() => {
    if (!toasts.length) return undefined;
    const timers = toasts.map((t) => window.setTimeout(() => dismissToast(t.key), AUTO_DISMISS_MS));
    return () => timers.forEach((id) => window.clearTimeout(id));
  }, [toasts, dismissToast]);

  const removeOfferLocally = useCallback((table, id) => {
    const k = `${table}:${id}`;
    stopRingForKey(k);
    // The OS notification carries live Accept / Decline buttons. Once this driver
    // has answered the offer anywhere (screen, home card, notification), a stale
    // row in the shade would answer "no longer available" later. Same tag the
    // sender uses: `ingo-offer-<table:id>`. Found 2026-09-03 after a decline on
    // the offer screen left the notification behind.
    withdrawOfferNotification(`ingo-offer-${k}`);
    setOffers((prev) => prev.filter((o) => offerKey(o) !== k));
    knownOfferKeysRef.current.delete(k);
    cycleRingedRef.current.delete(k);
    dismissToast(k);
  }, [dismissToast, stopRingForKey]);

  const refreshOffers = useCallback(async () => {
    await tickRef.current();
  }, []);

  /**
   * The one accept path (the offer screen, the home card, the notification's
   * button): InGo's accept by the offer's id, then the job it became, opened.
   * An offer another rider took, or that closed, says so; nothing else is
   * inferred from the error text.
   */
  const acceptOffer = useCallback(
    async (offer) => {
      const k = offerKey(offer);
      console.info(`[DriverOffers] accept ${k}`);
      const res = await acceptApiOffer(work, offer);
      console.info(`[DriverOffers] accept result ${JSON.stringify({ key: k, ok: res.ok, taken: Boolean(res.taken) })}`);
      if (!res.ok) {
        if (res.taken) {
          removeOfferLocally(offer.table, offer.id);
          setTakenNotice(res.error || ORDER_ALREADY_ACCEPTED_MSG);
        }
        return res;
      }
      removeOfferLocally(offer.table, offer.id);
      // Opened here: the poll must not open it a second time.
      markDriverAutoOpenedAcceptedDelivery(API_DELIVERIES, offer.id);
      if (res.job) {
        navigateRef.current(jobPath('active-delivery', res.job), { state: { order: res.job } });
      } else {
        // Taken, but not read back yet: opened by its key, and the job screen reads it.
        navigateRef.current(jobPath('active-delivery', { bookingTable: API_DELIVERIES, deliveryId: offer.id }));
      }
      return res;
    },
    [removeOfferLocally, work],
  );

  /** The one decline path: the next riders are asked at once. */
  const declineOffer = useCallback(
    async (offer) => {
      const res = await declineApiOffer(work, offer);
      console.info(`[DriverOffers] decline result ${JSON.stringify({ key: offerKey(offer), ...res })}`);
      if (res.ok) removeOfferLocally(offer.table, offer.id);
      return res;
    },
    [removeOfferLocally, work],
  );

  // Accept / Decline pressed on the notification: the same acceptOffer /
  // declineOffer the in-app card and the offer screen run, so there is one
  // accept path, not two. Waits up to NOTIFICATION_ACTION_WAIT_MS for the poll to
  // surface the offer, then reports it gone. Every outcome is logged and shown.
  useEffect(() => {
    const p = pendingActionRef.current;
    if (!p || !driverId) return undefined;
    const offer = findOfferForKey(offers, p.offerKey, p.tag);
    if (!offer) {
      // Give up only once the poll has completed twice since the tap without
      // returning the offer, or at the hard cap. A cold start's first poll can
      // take longer than any fixed wait: on 2026-09-03 the offer arrived at
      // ~25 s and a 20 s clock reported it gone while it was still open.
      const pollsSince = pollsDoneRef.current - (p.pollsAtPark || 0);
      const left = NOTIFICATION_ACTION_WAIT_MS - (Date.now() - p.at);
      if (pollsSince < 2 && left > 0) {
        const t = setTimeout(() => setActionTick((n) => n + 1), left + 50);
        return () => clearTimeout(t);
      }
      pendingActionRef.current = null;
      console.info(`[DriverOffers] notification button: offer not found ${JSON.stringify(p)}`);
      withdrawOfferNotification(p.tag);
      setActionNotice('That request is no longer open. It was already answered, taken by another driver, or timed out.');
      return undefined;
    }
    pendingActionRef.current = null;
    void (async () => {
      console.info(`[DriverOffers] notification button ${p.action} -> ${offerKey(offer)}`);
      withdrawOfferNotification(p.tag);
      if (p.action === 'decline') {
        const res = await declineOffer(offer);
        setActionNotice(res.ok ? 'Declined. You will not be asked about this request again.' : res.error || 'Could not save your decline.');
        return;
      }
      const res = await acceptOffer(offer);
      if (!res.ok && !res.taken) setActionNotice(res.error || 'Could not accept this request.');
    })();
    return undefined;
  }, [offers, actionTick, driverId, acceptOffer, declineOffer]);

  const value = useMemo(
    () => ({
      online,
      setOnline,
      live,
      offers,
      recent,
      setRecent,
      loadErr,
      ringingOfferKeys,
      driverId,
      removeOfferLocally,
      refreshOffers,
      setTakenNotice,
      acceptOffer,
      declineOffer,
    }),
    [
      online,
      setOnline,
      live,
      offers,
      recent,
      loadErr,
      ringingOfferKeys,
      driverId,
      removeOfferLocally,
      refreshOffers,
      acceptOffer,
      declineOffer,
    ],
  );

  return (
    <DriverOffersContext.Provider value={value}>
      {children}
      {toasts.length > 0 ? (
        <div className="drvNoteStack" role="region" aria-label="New delivery requests">
          {toasts.map((t) => (
            <div key={t.key} className="drvNote" role="alert">
              <span className="drvNote__pulse" aria-hidden />
              <div className="drvNote__icon" aria-hidden>
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
                  <path
                    d="M12 3 4 7v10l8 4 8-4V7l-8-4Z"
                    stroke="currentColor"
                    strokeWidth="1.6"
                    strokeLinejoin="round"
                  />
                  <path d="m4 7 8 4 8-4M12 11v10" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
                </svg>
              </div>
              <div className="drvNote__body">
                <p className="drvNote__title">{t.title}</p>
                <p className="drvNote__meta">
                  {t.ref} · {t.pickup} → {t.dropoff}
                  {t.amount ? ` · ${t.amount}` : ''}
                </p>
                <button
                  type="button"
                  className="drvNote__cta"
                  onClick={() => {
                    dismissToast(t.key);
                    unlockDriverOfferAudio();
                    navigate(offerPath(t.key));
                  }}
                >
                  View request
                </button>
              </div>
              <button
                type="button"
                className="drvNote__close"
                aria-label="Dismiss"
                onClick={() => dismissToast(t.key)}
              >
                <IconClose size={18} />
              </button>
            </div>
          ))}
        </div>
      ) : null}
      <ConfirmDialog
        open={Boolean(takenNotice)}
        title="Order already accepted"
        message={takenNotice || ORDER_ALREADY_ACCEPTED_MSG}
        confirmLabel="OK"
        hideCancel
        danger={false}
        onConfirm={() => setTakenNotice('')}
        onCancel={() => setTakenNotice('')}
      />
      <ConfirmDialog
        open={Boolean(dutyNotice)}
        title="Duty"
        message={dutyNotice}
        confirmLabel="OK"
        hideCancel
        danger={false}
        onConfirm={() => setDutyNotice('')}
        onCancel={() => setDutyNotice('')}
      />
      <ConfirmDialog
        open={positionsAsk}
        title="Sharing your location"
        message="While you are online, InGo shares this phone's location with the office, and with the client whose parcel you carry, even when the phone is locked or the app is closed. A notice stays in your notifications while it does. It stops when you go offline."
        confirmLabel="Continue"
        cancelLabel="Not now"
        danger={false}
        onConfirm={() => {
          markPositionsDisclosed();
          setPositionsAsk(false);
          void startOnDuty();
        }}
        onCancel={() => setPositionsAsk(false)}
      />
      <ConfirmDialog
        open={Boolean(batteryNotice) && !positionsAsk}
        title="Keep InGo running"
        message={batteryNotice}
        confirmLabel="Open settings"
        cancelLabel="Not now"
        danger={false}
        onConfirm={() => {
          setBatteryNotice('');
          void openBatterySettings();
        }}
        onCancel={() => setBatteryNotice('')}
      />
      <ConfirmDialog
        open={Boolean(actionNotice)}
        title="Delivery request"
        message={actionNotice}
        confirmLabel="OK"
        hideCancel
        danger={false}
        onConfirm={() => setActionNotice('')}
        onCancel={() => setActionNotice('')}
      />
    </DriverOffersContext.Provider>
  );
}

export function useDriverOffers() {
  const ctx = useContext(DriverOffersContext);
  if (!ctx) {
    throw new Error('useDriverOffers must be used within DriverOffersProvider');
  }
  return ctx;
}
